import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext, useMemo } from 'react';
import { filter } from 'lodash';

import StatusAddon from '../../../dataViews/NextTree/addons/StatusAddon';
import orderBy from '../../../../../common/utils/orderBy';

import Icon from '../../../utils/Icon';
import TranslationContext from '../../../utils/Translations/TranslationContext';

import styles from './TreeSelectorTooltipContent.scss';
import Statuses, {
  Active,
  Deleted,
} from '../../../../../model/StatusWithDraft';

const TreeSelectorTooltipContentItem = ({
  id,
  tree,
  selectedIds,
  isFormField,
  resolveNodeTitle,
  resolveIsNodeSelected,
  t,
  resolveNodeClassName,
}) => {
  const { direction } = useContext(TranslationContext);
  const { models, children } = tree;

  const node = models[id];

  const selected = useMemo(
    () =>
      resolveIsNodeSelected
        ? resolveIsNodeSelected({ selectedIds, children, currentNode: node })
        : selectedIds.has(id),
    [resolveIsNodeSelected, selectedIds, children, node, id],
  );

  const nodeClassName = useMemo(
    () => (resolveNodeClassName && node ? resolveNodeClassName(node) : ''),
    [resolveNodeClassName, node],
  );

  const childrenArray = useMemo(
    () =>
      orderBy(
        filter(children[id] || [], item => !!models[item]),
        [
          item => models[item]?.sequence,
          item => resolveNodeTitle(models[item]),
        ],
      ),
    [models, children, id, resolveNodeTitle],
  );

  if (!node) {
    return null;
  }

  return (
    <li
      className={classnames(
        'jstree-tooltip-node',
        { [styles.selected]: isFormField && selected },
        { [styles.unselected]: !selected },
        { [styles.listItem]: isFormField },
        nodeClassName,
      )}
      data-layout-direction={direction}
      dir={direction}
    >
      <>
        {!isFormField && selected ? (
          <Icon className="pr-5" name="checkmark3" />
        ) : null}
        <label title={resolveNodeTitle(node)}>{resolveNodeTitle(node)}</label>
        <StatusAddon
          customActiveStatus={Active}
          customDeletedStatus={Deleted}
          customStatuses={Statuses.BasicByValue}
          status={node.status}
        />
        {childrenArray ? (
          <ul className={styles.list}>
            {childrenArray.map(childId => (
              <TreeSelectorTooltipContentItem
                key={childId}
                id={childId}
                isFormField={isFormField}
                resolveIsNodeSelected={resolveIsNodeSelected}
                resolveNodeClassName={resolveNodeClassName}
                resolveNodeTitle={resolveNodeTitle}
                selectedIds={selectedIds}
                t={t}
                tree={tree}
              />
            ))}
          </ul>
        ) : null}
      </>
    </li>
  );
};

TreeSelectorTooltipContentItem.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  tree: PropTypes.shape({
    models: PropTypes.object.isRequired,
    plugins: PropTypes.object,
    children: PropTypes.object.isRequired,
  }).isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  isFormField: PropTypes.bool,
  resolveNodeTitle: PropTypes.func,
  getChildrenCount: PropTypes.func,
  resolveIsNodeSelected: PropTypes.func,
  t: PropTypes.func.isRequired,
  resolveNodeClassName: PropTypes.func,
};

TreeSelectorTooltipContentItem.defaultProps = {
  isFormField: false,
  resolveNodeTitle: node => node.name,
  getChildrenCount: null,
  resolveIsNodeSelected: null,
  resolveNodeClassName: null,
};

export default TreeSelectorTooltipContentItem;
